import { Capacitor, registerPlugin } from '@capacitor/core'

// Android native widget plugin
interface MiacWidgetPlugin {
  updateHabitState(options: { json: string }): Promise<void>
  updatePomodoroState(options: { json: string }): Promise<void>
  updateTrackerState(options: { json: string }): Promise<void>
  updateThemeState(options: { json: string }): Promise<void>
  updateAuthState(options: { json: string }): Promise<void>
  updateApiBaseUrl(options: { url: string }): Promise<void>
  clearAuthState(): Promise<void>
  refreshWidgets(): Promise<void>
}

let androidPlugin: MiacWidgetPlugin | null = null

function getAndroidPlugin(): MiacWidgetPlugin {
  if (!androidPlugin) {
    androidPlugin = registerPlugin<MiacWidgetPlugin>('MiacWidget')
  }
  return androidPlugin
}

type PrefsPlugin = typeof import('@capacitor/preferences').Preferences
let _cachedPrefsResult: { plugin: PrefsPlugin } | null | undefined

async function getPreferences(): Promise<{ plugin: PrefsPlugin } | null> {
  if (_cachedPrefsResult !== undefined) return _cachedPrefsResult
  try {
    const { Preferences } = await import('@capacitor/preferences')
    _cachedPrefsResult = { plugin: Preferences }
  } catch (e) {
    console.warn('[WidgetData] Preferences plugin unavailable:', e)
    _cachedPrefsResult = null
  }
  return _cachedPrefsResult
}

function isNative(): boolean {
  return Capacitor.isNativePlatform()
}

function isWidgetSupported(): boolean {
  return Capacitor.isNativePlatform()
}

function isIos(): boolean {
  return Capacitor.getPlatform() === 'ios'
}

function isAndroid(): boolean {
  return Capacitor.getPlatform() === 'android'
}

// ============================================================================
// Native Widget Refresh
// ============================================================================

export async function refreshWidgets(): Promise<boolean> {
  if (!isWidgetSupported()) return false

  try {
    if (isAndroid()) {
      await getAndroidPlugin().refreshWidgets()
      return true
    }
    // iOS: WidgetKit timeline policy handles refresh automatically
    console.log('Widget data updated - iOS will refresh widgets automatically')
    return true
  } catch (e) {
    console.warn('Failed to refresh widgets:', e)
    return false
  }
}

// ============================================================================
// Habit Data for Widget
// ============================================================================

export interface HabitWidgetData {
  id: string
  title: string
  emoji: string | null
  streak: number
  streakBest: number
  isCompletedToday: boolean
  requiresProof: boolean
  cardColor: string | null
  yearlyGrid: number[]
  isCompletable: boolean
  targetStreak: number | null
  conqueredAt: string | null
}

export interface WidgetHabitState {
  lastUpdated: number
  habits: HabitWidgetData[]
  habitsAtRisk: HabitWidgetData[]
  midnightTimestamp: number
  userTimezone: string
}

export interface WidgetAuthState {
  accessToken: string
  refreshToken: string
  expiresAt: number
  lastUpdated: number
}

export async function updateWidgetHabitData(
  habits: HabitWidgetData[]
): Promise<boolean> {
  if (!isWidgetSupported()) return false

  try {
    const midnight = new Date()
    midnight.setHours(24, 0, 0, 0)

    const habitsAtRisk = habits.filter(h => !h.isCompletedToday && h.streak > 0)

    const state: WidgetHabitState = {
      lastUpdated: Date.now(),
      habits,
      habitsAtRisk,
      midnightTimestamp: midnight.getTime(),
      userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }

    const json = JSON.stringify(state)

    if (isAndroid()) {
      await getAndroidPlugin().updateHabitState({ json })
    } else {
      const prefs = await getPreferences()
      if (!prefs) return false
      await prefs.plugin.set({ key: 'widget_habit_state', value: json })
    }

    console.log('Updated widget habit data:', habits.length, 'habits,', habitsAtRisk.length, 'at risk')
    return true
  } catch (e) {
    console.error('Failed to update widget habit data:', e)
    return false
  }
}

export async function updateWidgetAuthToken(
  accessToken: string,
  refreshToken: string,
  expiresAt: number
): Promise<boolean> {
  if (!isWidgetSupported()) return false

  try {
    const state: WidgetAuthState = {
      accessToken,
      refreshToken,
      expiresAt,
      lastUpdated: Date.now(),
    }

    const json = JSON.stringify(state)

    if (isAndroid()) {
      await getAndroidPlugin().updateAuthState({ json })
    } else {
      const prefs = await getPreferences()
      if (!prefs) return false
      await prefs.plugin.set({ key: 'widget_auth_state', value: json })
    }

    console.log('Updated widget auth token, expires:', new Date(expiresAt * 1000).toISOString())
    return true
  } catch (e) {
    console.error('Failed to update widget auth token:', e)
    return false
  }
}

export async function clearWidgetAuthToken(): Promise<boolean> {
  if (!isWidgetSupported()) return false

  try {
    if (isAndroid()) {
      await getAndroidPlugin().clearAuthState()
    } else {
      const prefs = await getPreferences()
      if (!prefs) return false
      await prefs.plugin.remove({ key: 'widget_auth_state' })
    }
    return true
  } catch (e) {
    console.error('Failed to clear widget auth token:', e)
    return false
  }
}

export async function updateWidgetApiBaseUrl(apiBaseUrl: string): Promise<boolean> {
  if (!isWidgetSupported()) return false

  try {
    if (isAndroid()) {
      await getAndroidPlugin().updateApiBaseUrl({ url: apiBaseUrl })
    } else {
      const prefs = await getPreferences()
      if (!prefs) return false
      await prefs.plugin.set({ key: 'widget_api_base_url', value: apiBaseUrl })
    }
    console.log('Updated widget API base URL:', apiBaseUrl)
    return true
  } catch (e) {
    console.error('Failed to update widget API base URL:', e)
    return false
  }
}

export async function getWidgetHabitData(): Promise<WidgetHabitState | null> {
  if (!isNative() || !isIos()) return null

  try {
    const prefs = await getPreferences()
    if (!prefs) return null
    const { value } = await prefs.plugin.get({ key: 'widget_habit_state' })
    if (!value) return null
    return JSON.parse(value) as WidgetHabitState
  } catch (e) {
    console.error('Failed to get widget habit data:', e)
    return null
  }
}

export async function clearWidgetHabitData(): Promise<boolean> {
  if (!isWidgetSupported()) return false

  try {
    if (isAndroid()) {
      const emptyState: WidgetHabitState = {
        lastUpdated: Date.now(),
        habits: [],
        habitsAtRisk: [],
        midnightTimestamp: 0,
        userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }
      await getAndroidPlugin().updateHabitState({ json: JSON.stringify(emptyState) })
      await getAndroidPlugin().refreshWidgets()
    } else if (isIos()) {
      const prefs = await getPreferences()
      if (!prefs) return false
      await prefs.plugin.remove({ key: 'widget_habit_state' })
    }
    return true
  } catch (e) {
    console.error('Failed to clear widget habit data:', e)
    return false
  }
}

// ============================================================================
// Tracker Data for Widget
// ============================================================================

export interface TrackerWidgetData {
  id: string
  title: string
  emoji: string | null
  unit: string | null
  todayTotal: number
  cardColor: string | null
  defaultValue: number
  stepSize: number
}

export interface WidgetTrackerState {
  lastUpdated: number
  trackers: TrackerWidgetData[]
}

export async function updateWidgetTrackerData(
  trackers: TrackerWidgetData[]
): Promise<boolean> {
  if (!isWidgetSupported()) return false

  try {
    const state: WidgetTrackerState = {
      lastUpdated: Date.now(),
      trackers,
    }

    const json = JSON.stringify(state)

    if (isAndroid()) {
      await getAndroidPlugin().updateTrackerState({ json })
    } else {
      const prefs = await getPreferences()
      if (!prefs) return false
      await prefs.plugin.set({ key: 'widget_tracker_state', value: json })
    }

    console.log('Updated widget tracker data:', trackers.length, 'trackers')
    return true
  } catch (e) {
    console.error('Failed to update widget tracker data:', e)
    return false
  }
}

// ============================================================================
// Pomodoro State for Widget
// ============================================================================

export interface WidgetPomodoroState {
  lastUpdated: number
  isRunning: boolean
  sessionType: 'work' | 'short_break' | 'long_break' | null
  startedAt: number | null
  durationSeconds: number | null
  completedToday: number
}

export async function updateWidgetPomodoroData(
  state: Omit<WidgetPomodoroState, 'lastUpdated'>
): Promise<boolean> {
  if (!isWidgetSupported()) return false

  try {
    const fullState: WidgetPomodoroState = {
      ...state,
      lastUpdated: Date.now(),
    }

    const json = JSON.stringify(fullState)

    if (isAndroid()) {
      await getAndroidPlugin().updatePomodoroState({ json })
    } else {
      const prefs = await getPreferences()
      if (!prefs) return false
      await prefs.plugin.set({ key: 'widget_pomodoro_state', value: json })
    }

    console.log('Updated widget pomodoro data:', state.completedToday, 'sessions today')
    return true
  } catch (e) {
    console.error('Failed to update widget pomodoro data:', e)
    return false
  }
}

export async function getWidgetPomodoroData(): Promise<WidgetPomodoroState | null> {
  if (!isNative() || !isIos()) return null

  try {
    const prefs = await getPreferences()
    if (!prefs) return null
    const { value } = await prefs.plugin.get({ key: 'widget_pomodoro_state' })
    if (!value) return null
    return JSON.parse(value) as WidgetPomodoroState
  } catch (e) {
    console.error('Failed to get widget pomodoro data:', e)
    return null
  }
}

// ============================================================================
// Theme State for Widget
// ============================================================================

export interface WidgetThemeState {
  isDarkMode: boolean
  accentColor: string
  lastUpdated: number
}

export async function updateWidgetThemeData(
  isDarkMode: boolean,
  accentColor: string = '#22c55e'
): Promise<boolean> {
  if (!isWidgetSupported()) return false

  try {
    const state: WidgetThemeState = {
      isDarkMode,
      accentColor,
      lastUpdated: Date.now(),
    }

    const json = JSON.stringify(state)

    if (isAndroid()) {
      await getAndroidPlugin().updateThemeState({ json })
    } else {
      const prefs = await getPreferences()
      if (!prefs) return false
      await prefs.plugin.set({ key: 'widget_theme_state', value: json })
    }

    console.log('Updated widget theme:', isDarkMode ? 'dark' : 'light', accentColor)
    return true
  } catch (e) {
    console.error('Failed to update widget theme data:', e)
    return false
  }
}

/**
 * Sync all widget data at once
 */
export async function syncAllWidgetData(params: {
  habits: HabitWidgetData[]
  pomodoro: Omit<WidgetPomodoroState, 'lastUpdated'>
  isDarkMode: boolean
  accentColor?: string
  auth?: { accessToken: string; refreshToken: string; expiresAt: number }
}): Promise<void> {
  const { habits, pomodoro, isDarkMode, accentColor, auth } = params
  
  const promises: Promise<boolean>[] = [
    updateWidgetHabitData(habits),
    updateWidgetPomodoroData(pomodoro),
    updateWidgetThemeData(isDarkMode, accentColor),
  ]
  
  if (auth) {
    promises.push(updateWidgetAuthToken(auth.accessToken, auth.refreshToken, auth.expiresAt))
  }
  
  await Promise.all(promises)
}
